import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { inventoryApi, normalizePaginatedResponse, productsApi } from '../services/api';
import ProductForm from '../components/inventory/ProductForm';

function currency(v) {
    return `₦${Number(v || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function quantityTone(product) {
    const qty = Number(product.quantity_in_stock || 0);
    const reorder = Number(product.reorder_level || 10);
    if (qty <= reorder) return 'danger';
    if (qty <= reorder * 2) return 'warn';
    return 'good';
}

function ProductModal({ mode, product, onClose, onSave, saving }) {
    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <div>
                        <h3>{mode === 'edit' ? 'Update Product' : 'Add Product'}</h3>
                        <p>{mode === 'edit' ? 'Edit product information and stock defaults.' : 'Create a new product in inventory.'}</p>
                    </div>
                    <button className="icon-btn" onClick={onClose} type="button">✕</button>
                </div>

                <ProductForm
                    product={product}
                    saving={saving}
                    onSubmit={onSave}
                    submitLabel={mode === 'edit' ? 'Save Changes' : 'Create Product'}
                />
            </div>
        </div>
    );
}

export default function InventoryPage() {
    const [products, setProducts] = useState([]);
    const [totalProducts, setTotalProducts] = useState(0);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('all');
    const [brand, setBrand] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [modal, setModal] = useState({ open: false, mode: 'create', product: null });
    const [adjustingId, setAdjustingId] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [lowStockCount, setLowStockCount] = useState(0);

    const loadProducts = useCallback(async (pageArg = page, rowsArg = rowsPerPage) => {
        setLoading(true);
        setError('');

        const offset = (pageArg - 1) * rowsArg;

        const [productsRes, lowStockRes] = await Promise.all([
            productsApi.list({ limit: rowsArg, offset }),
            productsApi.lowStock(),
        ]);

        if (!productsRes.success) {
            setError(productsRes.error || 'Failed to load products');
            setLoading(false);
            return;
        }

        const normalized = normalizePaginatedResponse(productsRes);
        const list = normalized.rows;
        setProducts(list.map((item) => ({
            ...item,
            brand: item?.metadata?.brand || item?.brand || 'General',
            unit: item?.metadata?.unit || 'Pc',
            createdBy: item?.metadata?.created_by_name || 'System Admin',
        })));
        setTotalProducts(normalized.total);
        setLowStockCount((lowStockRes?.data || []).length || 0);
        setLoading(false);
    }, [page, rowsPerPage]);

    useEffect(() => {
        loadProducts(page, rowsPerPage);
    }, [loadProducts, page, rowsPerPage]);

    const categories = useMemo(() => ['all', ...new Set(products.map((item) => item.category).filter(Boolean))], [products]);
    const brands = useMemo(() => ['all', ...new Set(products.map((item) => item.brand).filter(Boolean))], [products]);

    const filteredProducts = useMemo(() => {
        const q = search.trim().toLowerCase();
        return products.filter((item) => {
            const matchesSearch = !q || [item.name, item.sku, item.barcode, item.category, item.brand]
                .filter(Boolean)
                .some((value) => value.toString().toLowerCase().includes(q));
            const matchesCategory = category === 'all' || item.category === category;
            const matchesBrand = brand === 'all' || item.brand === brand;
            return matchesSearch && matchesCategory && matchesBrand;
        });
    }, [products, search, category, brand]);

    const openCreate = () => setModal({ open: true, mode: 'create', product: null });
    const openEdit = (product) => setModal({ open: true, mode: 'edit', product });
    const closeModal = () => setModal({ open: false, mode: 'create', product: null });

    const saveProduct = async (payload) => {
        setSaving(true);
        const response = modal.mode === 'edit'
            ? await productsApi.update(modal.product.id, payload)
            : await productsApi.create(payload);
        setSaving(false);

        if (!response.success) {
            alert(response.error || 'Unable to save product');
            return;
        }

        closeModal();
        loadProducts(page, rowsPerPage);
    };

    const adjustStock = async (product, type) => {
        const amount = Number(window.prompt(`Enter quantity to ${type === 'add' ? 'add to' : 'subtract from'} ${product.name}`));
        if (!amount || amount <= 0) return;

        setAdjustingId(product.id);
        const response = await inventoryApi.adjustStock({
            product_id: product.id,
            quantity: amount,
            adjustment_type: type,
            reason: `Manual ${type} adjustment from products page`,
        });
        setAdjustingId(null);

        if (!response.success) {
            alert(response.error || 'Stock adjustment failed');
            return;
        }
        loadProducts(page, rowsPerPage);
    };

    const toggleSelection = (id) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]);
    };

    const toggleSelectAll = () => {
        setSelectedIds((prev) => prev.length === filteredProducts.length ? [] : filteredProducts.map((item) => item.id));
    };

    const totalPages = Math.max(1, Math.ceil(totalProducts / rowsPerPage));
    const hasPrev = page > 1;
    const hasNext = page < totalPages;

    return (
        <div className="products-page">
            <div className="page-header products-header">
                <div>
                    <h2>Products</h2>
                    <p>Dashboard <span className="crumb-sep">›</span> Products</p>
                </div>

                <div className="products-toolbar-actions">
                    <button className="mini-icon-btn" type="button">🧾</button>
                    <button className="mini-icon-btn" type="button">📗</button>
                    <button className="mini-icon-btn" type="button" onClick={() => loadProducts(page, rowsPerPage)}>↻</button>
                    <button className="mini-icon-btn" type="button">⌃</button>
                    <button className="btn" type="button" onClick={openCreate}>Add Product</button>
                    <button className="btn btn-dark" type="button">Import Product</button>
                </div>
            </div>

            <section className="products-shell">
                <div className="products-filters">
                    <div className="products-search">
                        <span>⌕</span>
                        <input
                            placeholder="Search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="products-filter-right">
                        <select value={category} onChange={(e) => setCategory(e.target.value)}>
                            {categories.map((item) => (
                                <option key={item} value={item}>{item === 'all' ? 'Category' : item}</option>
                            ))}
                        </select>
                        <select value={brand} onChange={(e) => setBrand(e.target.value)}>
                            {brands.map((item) => (
                                <option key={item} value={item}>{item === 'all' ? 'Brand' : item}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="products-stats-strip">
                    <div className="products-stat-box">
                        <span>Total Products</span>
                        <strong>{products.length}</strong>
                    </div>
                    <div className="products-stat-box warn">
                        <span>Low Stock</span>
                        <strong>{lowStockCount}</strong>
                    </div>
                    <div className="products-stat-box">
                        <span>Selected</span>
                        <strong>{selectedIds.length}</strong>
                    </div>
                </div>

                {error ? <div className="error-banner">{error}</div> : null}

                <div className="products-table-wrap">
                    <table className="products-table">
                        <thead>
                            <tr>
                                <th>
                                    <input
                                        type="checkbox"
                                        checked={filteredProducts.length > 0 && selectedIds.length === filteredProducts.length}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                <th>SKU</th>
                                <th>Product Name</th>
                                <th>Category</th>
                                <th>Brand</th>
                                <th>Price</th>
                                <th>Unit</th>
                                <th>Qty</th>
                                <th>Created By</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={10} className="dash-empty-row">Loading products...</td>
                                </tr>
                            ) : filteredProducts.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="dash-empty-row">No products found</td>
                                </tr>
                            ) : filteredProducts.map((product) => {
                                const tone = quantityTone(product);
                                const qtyClass = tone === 'danger' ? 'qty-badge danger' : tone === 'warn' ? 'qty-badge warn' : 'qty-badge success';
                                return (
                                    <tr key={product.id}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(product.id)}
                                                onChange={() => toggleSelection(product.id)}
                                            />
                                        </td>
                                        <td className="muted-cell">{product.sku || '-'}</td>
                                        <td>
                                            <div className="product-name-cell">
                                                <div className="product-thumb" style={{ overflow: 'hidden', padding: 0 }}>
                                                    {product.image_url ? (
                                                        <img
                                                            src={product.image_url}
                                                            alt={product.name}
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        (product.name || '?').slice(0, 1).toUpperCase()
                                                    )}
                                                </div>
                                                <div>
                                                    <strong>{product.name}</strong>
                                                    <p>{product.description || 'Product catalog item'}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{product.category || '-'}</td>
                                        <td>{product.brand || '-'}</td>
                                        <td>{currency(product.selling_price)}</td>
                                        <td>{product.unit}</td>
                                        <td><span className={qtyClass}>{product.quantity_in_stock || 0}</span></td>
                                        <td>{product.createdBy}</td>
                                        <td>
                                            <div className="product-actions">
                                                <button className="mini-icon-btn" type="button" title="Preview">◉</button>
                                                <button className="mini-icon-btn" type="button" title="Edit" onClick={() => openEdit(product)}>✎</button>
                                                <button
                                                    className="mini-icon-btn"
                                                    type="button"
                                                    title="Increase Stock"
                                                    disabled={adjustingId === product.id}
                                                    onClick={() => adjustStock(product, 'add')}
                                                >
                                                    ＋
                                                </button>
                                                <button
                                                    className="mini-icon-btn"
                                                    type="button"
                                                    title="Reduce Stock"
                                                    disabled={adjustingId === product.id}
                                                    onClick={() => adjustStock(product, 'subtract')}
                                                >
                                                    －
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="products-footer-bar">
                    <div className="products-footer-left">
                        <span>Row Per Page</span>
                        <select value={String(rowsPerPage)} onChange={(e) => {
                            const nextRows = Number(e.target.value || 10);
                            setRowsPerPage(nextRows);
                            setPage(1);
                        }}>
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                        </select>
                        <span>Entries</span>
                    </div>
                    <div className="products-pagination">
                        <button className="mini-icon-btn" type="button" disabled={!hasPrev} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
                        <span className="page-dot active">{page}</span>
                        <span className="page-dot">/ {totalPages}</span>
                        <button className="mini-icon-btn" type="button" disabled={!hasNext} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>›</button>
                    </div>
                </div>
            </section>

            {modal.open ? (
                <ProductModal
                    mode={modal.mode}
                    product={modal.product}
                    onClose={closeModal}
                    onSave={saveProduct}
                    saving={saving}
                />
            ) : null}
        </div>
    );
}
